import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getPublicCourses } from '../api/courses';
import { registerUser } from '../api/enrollments';
import { setPortalToken } from '../api/client';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import type { PublicSpeaker, RegistrationField } from '../types';

const BACKEND_URL = 'http://localhost:3001';

function formatEditionDate(dateStr: string, durationMinutes: number): string {
  const date = new Date(dateStr);
  const endDate = new Date(date.getTime() + durationMinutes * 60 * 1000);
  return (
    format(date, "EEEE d MMMM yyyy 'dalle' HH:mm", { locale: it }) +
    ' alle ' +
    format(endDate, 'HH:mm')
  );
}

function PrivacyModal({ onAccept, onClose }: { onAccept: () => void; onClose: () => void }) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9000,
        background: 'rgba(0,0,0,0.55)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: '1.5rem',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 640,
        maxHeight: '85vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        {/* Header */}
        <div style={{ padding: '1.5rem 2rem 1rem', borderBottom: '1px solid var(--gray-100)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 700, color: 'var(--pub-navy)', fontFamily: 'var(--font-public)' }}>
              Informativa sul trattamento dei dati personali
            </h2>
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.5rem', color: 'var(--gray-400)', lineHeight: 1, padding: '0 0.25rem' }}
            >×</button>
          </div>
          <p style={{ margin: '0.375rem 0 0', fontSize: '0.8125rem', color: 'var(--gray-500)', fontFamily: 'var(--font-public)' }}>
            Art. 22 – Progetto AdriaClimPlus · Interreg VI-A Italia-Croazia 2021-2027
          </p>
        </div>

        {/* Body scrollabile */}
        <div style={{ overflowY: 'auto', padding: '1.5rem 2rem', flex: 1, fontSize: '0.875rem', lineHeight: 1.7, color: '#374151', fontFamily: 'var(--font-public)' }}>
          <p>
            In base al Regolamento 2016/679/UE (<em>General Data Protection Regulation</em> – GDPR) "<em>ogni persona ha diritto alla protezione dei dati di carattere personale che la riguardano</em>". In conformità con quanto previsto dal D.Lgs. 196/2003 e dal Regolamento 2016/679/UE il trattamento dei dati personali è improntato ai principi di correttezza, liceità e trasparenza, nonché di tutela della riservatezza e dei diritti dell'interessato. Si informa che i dati trasmessi dai candidati saranno utilizzati dagli uffici esclusivamente per gli adempimenti connessi allo svolgimento della procedura selettiva.
          </p>

          <Section title="Finalità del trattamento dei dati">
            <p>
              Il trattamento dei dati ha la finalità di consentire lo svolgimento delle attività legate all'espletamento di attività formative nell'ambito del <strong>Progetto AdriaClimPlus - Interreg VI-A Italia-Croazia 2021-2027</strong>. I dati forniti dai partecipanti vengono raccolti e trattati per:
            </p>
            <ul style={{ margin: '0.5rem 0', paddingLeft: '1.25rem' }}>
              <li>la gestione di tutte le attività correlate all'organizzazione del corso di formazione (iscrizione e registrazione, predisposizione dell'elenco degli iscritti, gestione delle presenze durante il corso, svolgimento di prove, rilascio attestato di partecipazione);</li>
              <li>tenere informato il partecipante, tramite invio di comunicazioni e aggiornamenti, in merito al corso di formazione cui si è registrato, nonché rispetto a future edizioni del medesimo, anche successivamente alla conclusione dell'edizione in corso;</li>
              <li>l'invio di questionari finalizzati a rilevare il grado di soddisfazione del partecipante al termine dei corsi di formazione al quale ha preso parte;</li>
              <li>finalità statistiche, attraverso aggregazione ovvero anonimizzazione dei dati;</li>
              <li>attività di rendicontazione delle spese sostenute relative allo stesso nell'ambito del progetto AdriaClimPlus, secondo le modalità previste dal Programma di cooperazione transfrontaliera Interreg VI-A Italia-Croazia 2021/2027.</li>
            </ul>
          </Section>

          <Section title="Base giuridica del trattamento dei dati e natura del conferimento">
            <p>Il trattamento dei dati per la finalità sopra descritta viene effettuato in applicazione della disciplina contenuta nelle seguenti fonti:</p>
            <ol style={{ margin: '0.5rem 0', paddingLeft: '1.25rem' }}>
              <li>necessità del trattamento ai fini della stipula e dell'esecuzione del contratto, ovvero ai fini dell'esecuzione di misure precontrattuali adottate su richiesta dell'interessato (art. 6 par. 1 lett. b Regolamento UE 2016/679);</li>
              <li>necessità del trattamento per adempiere obblighi giuridici a cui è soggetto il titolare del trattamento (art. 6 par. 1 lett. c Regolamento UE 2016/679);</li>
              <li>necessità del trattamento per l'esecuzione di un compito di interesse pubblico o connesso all'esercizio di pubblici poteri di cui è investito il titolare del trattamento (art. 6 par. 1 lett. e GDPR).</li>
            </ol>
          </Section>

          <Section title="Titolare del trattamento dei dati">
            <p>
              Titolare del trattamento è la <strong>Regione Puglia</strong>, nella persona del Direttore del Dipartimento Ambiente, Paesaggio e Qualità Urbana, con sede in Via Gentile n. 52 – 70126 Bari.<br />
              PEO: <a href="mailto:dipartimento.ambiente.territorio@regione.puglia.it" style={{ color: 'var(--pub-navy)' }}>dipartimento.ambiente.territorio@regione.puglia.it</a><br />
              PEC: <a href="mailto:dipartimento.ambiente.territorio@pec.rupar.puglia.it" style={{ color: 'var(--pub-navy)' }}>dipartimento.ambiente.territorio@pec.rupar.puglia.it</a>
            </p>
          </Section>

          <Section title="Responsabile della protezione dei dati">
            <p>
              Il punto di contatto con il RPD è il seguente:{' '}
              <a href="mailto:rpd@regione.puglia.it" style={{ color: 'var(--pub-navy)' }}>rpd@regione.puglia.it</a>{' '}e{' '}
              <a href="mailto:marketing@switchomedia.it" style={{ color: 'var(--pub-navy)' }}>marketing@switchomedia.it</a>
            </p>
          </Section>

          <Section title="Soggetti ai quali i dati possono essere comunicati">
            <p>
              I dati potranno essere trattati dal personale della Regione Puglia - Dipartimento Ambiente, Paesaggio e Qualità Urbana e dal personale della società Switch On Comunicazione &amp; Media Srls che per conto della Regione cura l'organizzazione delle attività formative; i dati potranno essere altresì comunicati a collaboratori autonomi, professionisti, consulenti, che prestino attività di consulenza o assistenza in ordine alle attività legate al Progetto AdriaClimPlus, nonché alle Autorità competenti e agli organismi di controllo previsti nell'ambito del Programma di cooperazione transfrontaliera Interreg VI-A Italia-Croazia 2021/2027.
            </p>
          </Section>

          <Section title="Trasferimento in Paesi Terzi">
            <p>I dati personali trattati non saranno oggetto di trasferimento in Paesi terzi extraeuropei.</p>
          </Section>

          <Section title="Processo decisionale automatizzato">
            <p>Non è presente alcun processo decisionale completamente automatizzato, compresa la profilazione.</p>
          </Section>

          <Section title="Conferimento dei dati">
            <p>
              L'acquisizione dei dati ed il relativo trattamento sono obbligatori in relazione alle finalità sopradescritte. Ne consegue che l'eventuale rifiuto a fornirli potrà determinare l'impossibilità del Titolare del trattamento ad erogare il servizio richiesto.
            </p>
          </Section>

          <Section title="Periodo di conservazione dei dati">
            <p>
              Il periodo di conservazione, ai sensi dell'articolo 5, par. 1, lett. e) del Regolamento 2016/679/UE, è il tempo necessario al raggiungimento delle finalità del trattamento, nonché per gli eventuali adempimenti contrattuali, amministrativi e giurisdizionali conseguenti.
            </p>
          </Section>

          <Section title="Diritti degli interessati">
            <p>Ai sensi degli artt. 15 e seguenti del Regolamento UE 2016/679 l'interessato può esercitare i seguenti diritti:</p>
            <ul style={{ margin: '0.5rem 0', paddingLeft: '1.25rem' }}>
              <li><strong>Diritto d'accesso</strong> – ottenere conferma del trattamento e accesso ai dati personali (art. 15 GDPR);</li>
              <li><strong>Diritto di rettifica</strong> – ottenere la rettifica dei dati inesatti o l'integrazione di quelli incompleti (art. 16 GDPR);</li>
              <li><strong>Diritto alla cancellazione</strong> – ottenere la cancellazione dei dati, qualora sussistano i motivi specificati nell'art. 17 GDPR;</li>
              <li><strong>Diritto di limitazione di trattamento</strong> – ottenere la limitazione del trattamento (art. 18 GDPR);</li>
              <li><strong>Diritto alla portabilità dei dati</strong> – ricevere i dati in formato strutturato e leggibile da dispositivo automatico (art. 20 GDPR);</li>
              <li><strong>Diritto di opposizione</strong> – opporsi in qualsiasi momento al trattamento (art. 21 GDPR).</li>
            </ul>
          </Section>

          <Section title="Diritto di reclamo">
            <p>
              Gli interessati che ritengono che il trattamento avvenga in violazione del GDPR hanno il diritto di proporre reclamo al <strong>Garante per la Protezione dei dati personali</strong> (art. 77 Regolamento UE 2016/679):<br />
              Piazza Venezia, 11 – 00187 Roma –{' '}
              <a href="mailto:protocollo@gpdp.it" style={{ color: 'var(--pub-navy)' }}>protocollo@gpdp.it</a>
            </p>
          </Section>
        </div>

        {/* Footer */}
        <div style={{ padding: '1rem 2rem 1.5rem', borderTop: '1px solid var(--gray-100)', flexShrink: 0, display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
          <button onClick={onClose} className="btn btn-ghost">
            Chiudi
          </button>
          <button
            onClick={onAccept}
            className="btn btn-pub-cta"
            style={{ minWidth: 140 }}
          >
            Ho letto — Accetto
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--pub-navy)', marginBottom: '0.5rem', fontFamily: 'var(--font-public)' }}>
        {title}
      </h3>
      {children}
    </div>
  );
}

export default function RegistrationPage() {
  const navigate = useNavigate();

  const [nome, setNome] = useState('');
  const [cognome, setCognome] = useState('');
  const [email, setEmail] = useState('');
  const [azienda, setAzienda] = useState('');
  const [consenso, setConsenso] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [extraData, setExtraData] = useState<Record<string, string | boolean | number>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: courses = [], isLoading } = useQuery({
    queryKey: ['public-courses'],
    queryFn: getPublicCourses,
  });

  const hasSelectableEditions = courses.some(c =>
    c.editions.some(e => (e.spotsLeft ?? 1) > 0 || e.waitlistEnabled)
  );

  // Collect unique registration fields from all active courses (shown immediately)
  const customFields = useMemo<RegistrationField[]>(() => {
    const seen = new Set<string>();
    const result: RegistrationField[] = [];
    for (const course of courses) {
      for (const f of (course.registrationFields ?? [])) {
        if (!seen.has(f.key)) { seen.add(f.key); result.push(f); }
      }
    }
    return result;
  }, [courses]);

  // Auto-select all editions when "Intero corso" is chosen
  React.useEffect(() => {
    const val = extraData.tipo_partecipazione;
    if (val === 'Intero corso (8 giornate)') {
      const all = new Set(courses.flatMap(c => c.editions.map(e => e.id)));
      setSelectedIds(all);
      setErrors(e => ({ ...e, editions: '' }));
    }
  }, [extraData.tipo_partecipazione, courses]);

  // Fields moved to bottom of edition column (before submit)
  const BOTTOM_FIELD_KEYS = ['privacy', 'autorizzazione_foto'];

  // Filter by showIf conditions; exclude giornata_* and bottom fields (rendered elsewhere)
  const visibleCustomFields = useMemo<RegistrationField[]>(() => {
    return customFields.filter(f => {
      if (f.key.startsWith('giornata_')) return false;
      if (BOTTOM_FIELD_KEYS.includes(f.key)) return false;
      if (!f.showIf) return true;
      return String(extraData[f.showIf.key] ?? '') === f.showIf.value;
    });
  }, [customFields, extraData]);

  // Fields that render in the middle column, just above the submit button
  const bottomFields = useMemo<RegistrationField[]>(() => {
    return customFields.filter(f => BOTTOM_FIELD_KEYS.includes(f.key));
  }, [customFields]);

  const toggleEdition = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setErrors((e) => ({ ...e, editions: '' }));
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!nome.trim()) errs.nome = 'Il nome è obbligatorio';
    if (!cognome.trim()) errs.cognome = 'Il cognome è obbligatorio';
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      errs.email = 'Inserisci un indirizzo email valido';
    if (selectedIds.size === 0)
      errs.editions = 'Seleziona almeno un incontro';
    if (!consenso)
      errs.consenso = 'Il consenso al trattamento dei dati è obbligatorio';
    for (const f of visibleCustomFields) {
      if (f.required && !extraData[f.key])
        errs[`extra_${f.key}`] = `${f.label} è obbligatorio`;
    }
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const mergedExtra: Record<string, string | boolean | number> = {};
      for (const [k, v] of Object.entries(extraData)) {
        if (k.endsWith('__altro')) continue;
        if (v === 'Altro' && extraData[k + '__altro']) {
          mergedExtra[k] = `Altro: ${extraData[k + '__altro']}`;
        } else {
          mergedExtra[k] = v;
        }
      }
      const result = await registerUser({
        nome: nome.trim(),
        cognome: cognome.trim(),
        email: email.trim(),
        azienda: azienda.trim() || undefined,
        edition_ids: Array.from(selectedIds),
        consenso: true,
        ...(Object.keys(mergedExtra).length > 0 ? { extra_data: mergedExtra } : {}),
      });
      setPortalToken(result.accessToken);
      navigate('/confirmed');
    } catch (err: any) {
      setSubmitError(
        err?.response?.data?.error ?? 'Si è verificato un errore. Riprova tra qualche minuto.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalEditions = courses.reduce((acc, c) => acc + c.editions.length, 0);

  return (
    <div className="page-wrapper" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {showPrivacy && (
        <PrivacyModal
          onAccept={() => { setConsenso(true); setErrors((e) => ({ ...e, consenso: '' })); setShowPrivacy(false); }}
          onClose={() => setShowPrivacy(false)}
        />
      )}
      {/* ── Dark nav bar ── */}
      <div className="public-nav-bar">
        <div className="public-nav-bar-inner">
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8125rem', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600 }}>
            Portale Iscrizioni
          </span>
          <a href="/accesso" style={{ marginLeft: 'auto' }}>Sei già iscritto?</a>
        </div>
      </div>

      {/* ── Portal banner ── */}
      <div className="reg-banner" style={{ background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <picture style={{ width: '100%', height: '100%', display: 'block' }}>
          <source media="(max-width: 640px)" srcSet="/header-mobile.jpg" />
          <source media="(max-width: 1024px)" srcSet="/header-tablet.jpg" />
          <img
            src="/banner-form.jpg"
            alt="AdriaClimPlus"
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
          />
        </picture>
      </div>

      {/* ── Scarica programma ── */}
      {!isLoading && courses.some(c => c.programmaUrl) && (
        <div style={{ background: 'var(--pub-navy)', padding: '1rem 2rem', display: 'flex', justifyContent: 'center' }}>
          {courses.filter(c => c.programmaUrl).map(c => (
            <a
              key={c.id}
              href={`/uploads/programmi/${c.programmaUrl!.split('/').pop()}`}
              target="_blank"
              rel="noopener noreferrer"
              download
              style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem',
                background: '#fff', border: 'none',
                borderRadius: 10, padding: '0.75rem 2rem',
                color: 'var(--pub-navy)', textDecoration: 'none',
                fontFamily: 'var(--font-public)', fontSize: '1.25rem', fontWeight: 700,
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Scarica il programma
            </a>
          ))}
        </div>
      )}

      {/* ── Two-half layout ── */}
      <div style={{ display: 'flex', flex: 1 }}>
        {!isLoading && !hasSelectableEditions ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4rem 2rem' }}>
            <div style={{ textAlign: 'center', maxWidth: 480 }}>
              <p className="public-page-title" style={{ marginBottom: '1rem' }}>Iscrizioni non disponibili</p>
              <p style={{ color: 'var(--gray-500)', fontFamily: 'var(--font-public)', fontSize: '0.9375rem', lineHeight: 1.6 }}>
                Al momento non ci sono edizioni disponibili per l'iscrizione. Torna a visitare questa pagina in seguito.
              </p>
            </div>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="reg-form-layout">

          {/* ── Left half: white, personal info ── */}
          <div className="reg-form-left">
            <p className="public-page-title" style={{ marginBottom: '0.5rem' }}>Iscriviti</p>
            <p style={{ color: 'var(--gray-500)', marginBottom: '2.5rem', fontFamily: 'var(--font-public)', fontSize: '0.9375rem' }}>
              Compila i tuoi dati e seleziona gli incontri.
            </p>

                {submitError && (
                  <div className="alert alert-danger" style={{ marginBottom: '1.5rem' }}>
                    {submitError}
                  </div>
                )}

                <div className="reg-name-grid">
                  <div className="form-group">
                    <label className="form-label">Nome <span className="required">*</span></label>
                    <input
                      type="text"
                      className={`form-control ${errors.nome ? 'is-invalid' : ''}`}
                      value={nome}
                      onChange={(e) => { setNome(e.target.value); setErrors((err) => ({ ...err, nome: '' })); }}
                      placeholder="Mario"
                      autoComplete="given-name"
                    />
                    {errors.nome && <span className="form-error">{errors.nome}</span>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Cognome <span className="required">*</span></label>
                    <input
                      type="text"
                      className={`form-control ${errors.cognome ? 'is-invalid' : ''}`}
                      value={cognome}
                      onChange={(e) => { setCognome(e.target.value); setErrors((err) => ({ ...err, cognome: '' })); }}
                      placeholder="Rossi"
                      autoComplete="family-name"
                    />
                    {errors.cognome && <span className="form-error">{errors.cognome}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Email <span className="required">*</span></label>
                  <input
                    type="email"
                    className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setErrors((err) => ({ ...err, email: '' })); }}
                    placeholder="mario.rossi@azienda.it"
                    autoComplete="email"
                  />
                  {errors.email && <span className="form-error">{errors.email}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Azienda</label>
                  <input
                    type="text"
                    className="form-control"
                    value={azienda}
                    onChange={(e) => setAzienda(e.target.value)}
                    placeholder="Nome dell'azienda (opzionale)"
                    autoComplete="organization"
                  />
                </div>

                {visibleCustomFields.length > 0 && (
                  <div style={{ borderTop: '1px solid var(--gray-200)', paddingTop: '1rem', marginTop: '0.5rem' }}>
                    {visibleCustomFields.map(f => {
                      const errKey = `extra_${f.key}`;
                      const val = extraData[f.key] ?? (f.type === 'checkbox' ? false : '');
                      const setVal = (v: string | boolean | number) => {
                        setExtraData(d => ({ ...d, [f.key]: v }));
                        setErrors(e => ({ ...e, [errKey]: '' }));
                      };
                      return (
                        <div key={f.key} className="form-group">
                          {f.type !== 'checkbox' && (
                            <label className="form-label">
                              {f.label}{f.required && <span className="required"> *</span>}
                            </label>
                          )}
                          {f.type === 'textarea' && (
                            <textarea className={`form-control ${errors[errKey] ? 'is-invalid' : ''}`} rows={3}
                              value={val as string} onChange={e => setVal(e.target.value)}
                              placeholder={f.placeholder} style={{ resize: 'vertical' }} />
                          )}
                          {f.type === 'select' && (
                            <>
                              <select className={`form-control ${errors[errKey] ? 'is-invalid' : ''}`}
                                value={val as string} onChange={e => setVal(e.target.value)}>
                                <option value="">— Seleziona —</option>
                                {(f.options ?? []).map(o => <option key={o} value={o}>{o}</option>)}
                              </select>
                              {val === 'Altro' && (
                                <input type="text" className="form-control" style={{ marginTop: '0.5rem' }}
                                  placeholder="Specifica..."
                                  value={(extraData[f.key + '__altro'] ?? '') as string}
                                  onChange={e => setExtraData(d => ({ ...d, [f.key + '__altro']: e.target.value }))} />
                              )}
                            </>
                          )}
                          {f.type === 'checkbox' && (
                            <div className="form-check">
                              <input type="checkbox" className="form-check-input" id={`f_${f.key}`}
                                checked={val as boolean} onChange={e => setVal(e.target.checked)} />
                              <label htmlFor={`f_${f.key}`} className="form-check-label" style={{ fontSize: '0.875rem' }}>
                                {f.label}{f.required && <span className="required"> *</span>}
                              </label>
                            </div>
                          )}
                          {(f.type === 'text' || f.type === 'number') && (
                            <input type={f.type} className={`form-control ${errors[errKey] ? 'is-invalid' : ''}`}
                              value={val as string} onChange={e => setVal(f.type === 'number' ? Number(e.target.value) : e.target.value)}
                              placeholder={f.placeholder} />
                          )}
                          {f.type === 'date' && (
                            <input type="date" className={`form-control ${errors[errKey] ? 'is-invalid' : ''}`}
                              value={val as string} onChange={e => setVal(e.target.value)} />
                          )}
                          {errors[errKey] && <span className="form-error">{errors[errKey]}</span>}
                        </div>
                      );
                    })}
                  </div>
                )}

          </div>

          {/* ── Right half: gray, edition checklist ── */}
          <div className="reg-form-right">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.75rem' }}>
                  <p className="public-page-title" style={{ margin: 0, fontSize: '1.25rem' }}>Seleziona gli incontri</p>
                  {selectedIds.size > 0 && (
                    <span style={{ background: 'var(--pub-navy)', color: '#fff', borderRadius: '999px', padding: '0.25rem 0.875rem', fontSize: '0.8125rem', fontWeight: 700, fontFamily: 'var(--font-public)' }}>
                      {selectedIds.size} selezionati
                    </span>
                  )}
                </div>

                <p style={{ fontSize: '1rem', color: 'var(--gray-500)', fontFamily: 'var(--font-public)', marginBottom: '1.25rem', lineHeight: 1.5 }}>
                  Tutte le registrazioni delle lezioni, sia online che in presenza, saranno in seguito disponibili on demand su questo portale.
                </p>

                {errors.editions && (
                  <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>
                    {errors.editions}
                  </div>
                )}

                {isLoading ? (
                  <div className="loading-container"><div className="spinner" /></div>
                ) : courses.length === 0 ? (
                  <p style={{ color: 'var(--gray-500)', textAlign: 'center', padding: '2rem 0', fontFamily: 'var(--font-public)' }}>
                    Nessun corso disponibile al momento.
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                    {totalEditions > 1 && (
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => {
                            const all = new Set(courses.flatMap((c) => c.editions.map((e) => e.id)));
                            setSelectedIds(all);
                            setErrors((err) => ({ ...err, editions: '' }));
                          }}
                        >
                          Seleziona tutti
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => setSelectedIds(new Set())}
                        >
                          Deseleziona tutti
                        </button>
                      </div>
                    )}
                    {courses.map((course) => (
                      <div key={course.id}>
                        {/* Course header */}
                        {course.bannerUrl ? (
                          <div style={{ position: 'relative', marginBottom: '1rem', borderRadius: '14px', overflow: 'hidden' }}>
                            <img
                              src={`/uploads${course.bannerUrl!.replace(/^\/uploads/, '')}`}
                              alt={course.name}
                              style={{ width: '100%', height: '90px', objectFit: 'cover', display: 'block' }}
                            />
                            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,38,75,0.72) 0%, rgba(0,38,75,0.1) 100%)', display: 'flex', alignItems: 'flex-end', padding: '0.75rem 1rem' }}>
                              <span style={{ color: '#fff', fontWeight: 700, fontSize: '1rem', fontFamily: 'var(--font-public)' }}>
                                {course.name}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div style={{ background: 'var(--pub-navy)', borderRadius: '14px', padding: '0.875rem 1.125rem', marginBottom: '1rem' }}>
                            <span style={{ color: '#fff', fontWeight: 700, fontSize: '1rem', fontFamily: 'var(--font-public)' }}>
                              {course.name}
                            </span>
                          </div>
                        )}

                        {course.description && (
                          <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginBottom: '0.875rem', fontFamily: 'var(--font-public)' }}>
                            {course.description}
                          </p>
                        )}

                        {course.editions.length === 0 ? (
                          <p style={{ fontSize: '0.875rem', color: 'var(--gray-400)', fontStyle: 'italic', fontFamily: 'var(--font-public)' }}>
                            Nessuna edizione disponibile
                          </p>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                            {course.editions.map((edition) => {
                              const isSoldOut = edition.spotsLeft === 0 && !edition.waitlistEnabled;
                              const checked = selectedIds.has(edition.id);
                              return (
                                <label
                                  key={edition.id}
                                  className={`edition-card ${checked ? 'selected' : ''} ${isSoldOut ? 'disabled' : ''}`}
                                  style={isSoldOut ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    disabled={isSoldOut}
                                    onChange={() => !isSoldOut && toggleEdition(edition.id)}
                                    style={{ marginTop: '0.15rem', accentColor: 'var(--pub-navy)', width: '1.125rem', height: '1.125rem', flexShrink: 0 }}
                                  />
                                  <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                      <span style={{ fontWeight: 700, fontSize: '0.9375rem', color: 'var(--pub-navy)', fontFamily: 'var(--font-public)' }}>
                                        {edition.label}
                                      </span>
                                      {edition.inPresenza && (
                                        <span className="pub-tag" style={{ background: 'var(--pub-orange)', color: '#fff' }}>📍 In presenza</span>
                                      )}
                                      {isSoldOut ? (
                                        <span className="pub-tag pub-tag-red">Posti esauriti</span>
                                      ) : edition.spotsLeft === 0 ? (
                                        <span className="pub-tag pub-tag-orange">Lista d'attesa</span>
                                      ) : null}
                                    </div>
                                    <div style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', marginTop: '0.25rem', fontFamily: 'var(--font-public)' }}>
                                      {formatEditionDate(edition.scheduledAt, edition.durationMinutes)}
                                    </div>
                                    {edition.spotsLeft === 0 && checked && (
                                      <div style={{ fontSize: '0.75rem', color: 'var(--pub-orange-dark)', marginTop: '0.3rem', fontWeight: 500, fontFamily: 'var(--font-public)' }}>
                                        Sarai inserito in lista d'attesa per questo incontro.
                                      </div>
                                    )}
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}

                  </div>
                )}

                {/* Consenso unificato (GDPR + privacy + foto) */}
                <div className="form-group" style={{ marginTop: '2rem', borderTop: '1px solid var(--gray-200)', paddingTop: '1.25rem' }}>
                  <div className="form-check" style={{ alignItems: 'flex-start' }}>
                    <input
                      type="checkbox"
                      id="consenso"
                      className="form-check-input"
                      checked={consenso}
                      onChange={(e) => {
                        setConsenso(e.target.checked);
                        setErrors((err) => ({ ...err, consenso: '' }));
                        setExtraData(d => ({ ...d, privacy: e.target.checked, autorizzazione_foto: e.target.checked }));
                      }}
                      style={{ marginTop: '0.2rem', flexShrink: 0 }}
                    />
                    <label htmlFor="consenso" className="form-check-label" style={{ fontSize: '0.875rem', lineHeight: 1.5 }}>
                      Ho letto e accetto l'{' '}
                      <button
                        type="button"
                        onClick={() => setShowPrivacy(true)}
                        style={{ background: 'none', border: 'none', padding: 0, color: 'var(--pub-navy)', fontWeight: 600, cursor: 'pointer', fontSize: 'inherit', textDecoration: 'underline', textUnderlineOffset: 2 }}
                      >
                        Informativa sulla Privacy
                      </button>
                      , acconsento al trattamento dei miei dati personali ai sensi del GDPR (Reg. UE 2016/679) e autorizzo fotografie e riprese video.{' '}
                      <span className="required">*</span>
                    </label>
                  </div>
                  {errors.consenso && (
                    <span className="form-error" style={{ marginTop: '0.5rem', display: 'block' }}>
                      {errors.consenso}
                    </span>
                  )}
                </div>

                <div style={{ marginTop: '1.25rem' }}>
                  <button
                    type="submit"
                    className="btn btn-pub-cta btn-lg w-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner" style={{ width: '1rem', height: '1rem', borderWidth: '2px', borderTopColor: '#fff', borderColor: 'rgba(255,255,255,0.3)' }} />
                        Invio in corso...
                      </>
                    ) : selectedIds.size > 0 ? (
                      `Iscriviti — ${selectedIds.size} ${selectedIds.size === 1 ? 'incontro' : 'incontri'}`
                    ) : (
                      'Iscriviti'
                    )}
                  </button>
                </div>

                <p style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.8125rem', color: 'var(--gray-400)', fontFamily: 'var(--font-public)' }}>
                  I campi con <span style={{ color: 'var(--danger)' }}>*</span> sono obbligatori
                </p>
          </div>

          {/* ── Right column: speakers ── */}
          {!isLoading && courses.some(c => c.showSpeakers && c.speakers?.length) && (
            <div style={{ width: '600px', flexShrink: 0, background: '#fff', borderLeft: '1px solid var(--gray-100)', padding: '3rem 2.5rem', overflowY: 'auto' }}>
              <p className="public-page-title" style={{ marginBottom: '1.75rem', fontSize: '1.125rem' }}>I relatori</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
                {courses.map((course) => {
                  const speakers: PublicSpeaker[] = course.speakers ?? [];
                  if (!course.showSpeakers || speakers.length === 0) return null;

                  // Group speakers by edition
                  const editionOrder: string[] = [];
                  const byEdition: Record<string, { label: string; speakers: PublicSpeaker[] }> = {};
                  for (const s of speakers) {
                    if (!byEdition[s.edition_id]) {
                      editionOrder.push(s.edition_id);
                      byEdition[s.edition_id] = { label: s.edition_label, speakers: [] };
                    }
                    byEdition[s.edition_id].speakers.push(s);
                  }

                  return (
                    <div key={course.id}>
                      {editionOrder.map((editionId) => {
                        const group = byEdition[editionId];
                        return (
                          <div key={editionId} style={{ marginBottom: '1.75rem' }}>
                            <p style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--pub-navy)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.875rem', fontFamily: 'var(--font-public)' }}>
                              {group.label}
                            </p>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
                              {group.speakers.map((s) => (
                                <div key={`${editionId}-${s.id}`} style={{ display: 'flex', flexDirection: 'column', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--gray-100)' }}>
                                  {s.photo_url && (
                                    <div style={{ width: '100%', height: 260, overflow: 'hidden', flexShrink: 0 }}>
                                      <img src={`/uploads${s.photo_url.replace(/^\/uploads/, '')}`} alt={`${s.nome} ${s.cognome}`}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                    </div>
                                  )}
                                  <div style={{ padding: '0.875rem 1rem' }}>
                                    <div style={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--pub-navy)', fontFamily: 'var(--font-public)', lineHeight: 1.2 }}>
                                      {s.nome} {s.cognome}
                                    </div>
                                    {s.azienda && (
                                      <div style={{ fontSize: '0.8125rem', color: 'var(--pub-orange)', fontWeight: 500, marginTop: '0.25rem', fontFamily: 'var(--font-public)' }}>
                                        {s.azienda}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </form>
        )}
      </div>

      {/* ── Footer ── */}
      <footer style={{
        background: 'var(--pub-navy)', color: 'rgba(255,255,255,0.55)',
        fontSize: '0.8125rem', textAlign: 'center', padding: '1rem 2rem',
        fontFamily: 'var(--font-public)',
      }}>
        {import.meta.env.VITE_FOOTER_TEXT}
      </footer>
    </div>
  );
}
